import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { api, type ContainerRow } from "@/lib/api";
import { booChatStatus } from "@/lib/connection-status";
import { useQuery } from "@tanstack/react-query";
import { BooAppActions, BooAppPanel } from "./BooApp";
import { ConnectionHeader, Notice, StatusLine } from "./ConnectionParts";
import type { ConnectionDef } from "./connections-lib";
import { useBooApp } from "./use-boo-app";

// Microsoft Teams and Discord: the Boo app is the only way in, so the page is
// its header and the Boo app panel.

const COPY = {
  teams: {
    usage: "DM it, or @mention it in any channel where the app is installed.",
    pitch: "Add Boo to your Teams tenant. Your team can then DM it and @mention it in channels.",
  },
  discord: {
    usage: "DM it, or @mention it in any channel it can see.",
    pitch: "Add Boo to your Discord server. Your team can then DM it and @mention it in channels.",
  },
} as const;

export function SharedChatAppConnection({
  def,
  container,
  platform,
}: {
  def: ConnectionDef;
  container: ContainerRow;
  platform: "teams" | "discord";
}) {
  const app = useBooApp(platform, container);
  // Discord is offered only on builds whose channel report lists it: a Boo
  // can't update its own brain, so an older build would dead-end. Same query
  // key as the Channels tab, so the report is fetched once per Boo.
  const platforms = useQuery({
    queryKey: ["channel-platforms", container.id],
    queryFn: () => api.containers.channels.platforms(container.id),
    enabled: platform === "discord" && container.deploymentStatus === "running",
  });
  const status = booChatStatus(app.data, app.query.isLoading, app.place, container.id);
  const header = (
    <ConnectionHeader
      def={def}
      status={<StatusLine status={status} className="h-5 text-sm" />}
      actions={<BooAppActions app={app} container={container} />}
    />
  );

  const discordUnsupported =
    platform === "discord" && !platforms.isLoading && !platforms.data?.platforms.some((p) => p.platform === "discord");

  return (
    <>
      {header}
      {app.query.isLoading || (platform === "discord" && platforms.isLoading) ? (
        <LoadingIndicator message={`Checking ${app.config.label}…`} />
      ) : app.query.isError || !app.data ? (
        <InlineError error={app.query.error ?? `Failed to check ${app.config.label} status`} />
      ) : discordUnsupported ? (
        <Notice>
          <p>This Boo can join Discord once it&apos;s running and on its next update.</p>
        </Notice>
      ) : (
        <BooAppPanel app={app} usage={COPY[platform].usage} pitch={COPY[platform].pitch} />
      )}
    </>
  );
}
